import { cn } from '@utima/ui';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';

import { LockedFeatureTooltip } from '@/components/subscription/LockedFeatureTooltip';

export interface SegmentedControlOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  disabledHint?: string;
}

export interface SegmentedControlProps {
  value?: string;
  onChange: (value: string) => void;
  options: SegmentedControlOption[];
  disabled?: boolean;
  id?: string;
}

/**
 * A hand-built segmented switch (Chat / Voice style mode picker).
 *
 * `@utima/ui` has no RadioGroup / ToggleGroup / Tabs primitive, so this is
 * assembled from plain buttons styled with Tailwind, following the same
 * "wrap a FormControl render prop" idiom as `MultiSelect`.
 *
 * Supports a per-option disabled state with a short upgrade hint (used to
 * lock the Voice option behind a tier flag without hiding it entirely).
 */
export function SegmentedControl({
  value,
  onChange,
  options,
  disabled,
  id,
}: SegmentedControlProps) {
  return (
    <div
      id={id}
      role='radiogroup'
      className='inline-flex gap-1 rounded-radius border border-input-border bg-input p-1'
    >
      {options.map(option => {
        const isSelected = option.value === value;
        const isOptionDisabled = disabled || option.disabled;

        const button = (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={isSelected}
            disabled={isOptionDisabled}
            onClick={() => {
              if (isOptionDisabled) {
                return;
              }

              onChange(option.value);
            }}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              isSelected
                ? 'bg-primary text-primary-fg shadow-sm'
                : 'text-placeholder hover:text-input-fg',
              isOptionDisabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer',
            )}
          >
            {option.disabled && <Lock className='size-3.5' />}
            {option.label}
          </button>
        );

        if (option.disabled && option.disabledHint) {
          return (
            <LockedFeatureTooltip
              key={option.value}
              locked
              message={option.disabledHint}
            >
              {button}
            </LockedFeatureTooltip>
          );
        }

        return button;
      })}
    </div>
  );
}
